import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Schedule as PendingIcon,
  PlayArrow as PlayIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import AdminPage from "../AdminPage";
import { apiClient } from "../../utils/apiClient";
import { useSnackbar } from "../../components/SnackbarProvider";
import { useDispatch } from "react-redux";
import { openEmailModal, openShipmentModal, openDatabaseObjectModal } from "../../store/adminSlice";
import { DbObjectIdComponent } from "../../internal/components/DbObjectIdComponent";

import {
  type SavedResult,
  type TestSet,
  type PipelineStep,
  type PipelineResultLight,
  type PipelineResultFull,
  ALL_STEPS,
  PIPELINE_STEPS,
  getPipelineDiagnosticCounts,
} from "./components";
import { ResultsTable } from "./components/ResultsTable";
import { ResultDetailDrawer } from "./components/ResultDetailDrawer";
import { RunPipelineDialog } from "./components/RunPipelineDialog";
import { EditNameDialog } from "./components/EditNameDialog";
import { getValueColor } from "./components/colorizeEval";

interface EvalSummary {
  total: number;
  keys: Array<{
    key: string;
    values: Array<{ value: string; count: number }>;
  }>;
}

function computeEvalSummary(results: PipelineResultLight[]): EvalSummary {
  const keyValueCounts: Record<string, Record<string, number>> = {};
  let total = 0;

  for (const result of results) {
    if (result.evalStep?.output) {
      total++;
      for (const [key, value] of Object.entries(result.evalStep.output)) {
        if (!keyValueCounts[key]) {
          keyValueCounts[key] = {};
        }
        const strValue = String(value);
        keyValueCounts[key][strValue] = (keyValueCounts[key][strValue] || 0) + 1;
      }
    }
  }

  const keys = Object.entries(keyValueCounts).map(([key, valueCounts]) => {
    const values = Object.entries(valueCounts)
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 4); // Top 4 values
    return { key, values };
  });

  return { total, keys };
}

function truncateValue(value: string, maxLength: number = 20): string {
  if (value.length <= maxLength) return value;
  return value.substring(0, maxLength) + "…";
}

export function EmailAutomationPipelineResultsPage() {
  const { resultId } = useParams<{ resultId: string }>();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const [savedResult, setSavedResult] = useState<SavedResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedResult, setSelectedResult] = useState<PipelineResultLight | null>(null);
  const [detailedResult, setDetailedResult] = useState<PipelineResultFull | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editNameDialogOpen, setEditNameDialogOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [saving, setSaving] = useState(false);
  const [drawerTab, setDrawerTab] = useState(0);

  // Run Again dialog state
  const [runDialogOpen, setRunDialogOpen] = useState(false);
  const [testSets, setTestSets] = useState<TestSet[]>([]);
  const [selectedTestSetId, setSelectedTestSetId] = useState<string>("");
  const [emailIds, setEmailIds] = useState("");
  const [stepsToRun, setStepsToRun] = useState<PipelineStep[]>(ALL_STEPS);
  const [running, setRunning] = useState(false);
  const [runProgress, setRunProgress] = useState<{
    resultId: string;
    workflowId: string;
    processedCount: number;
    totalCount: number;
    status: string;
    isOverwrite: boolean;
  } | null>(null);
  const [overwriteResults, setOverwriteResults] = useState(true);
  const [inputMode, setInputMode] = useState<"emailIds" | "jsonInputs">("emailIds");
  const [inputsJson, setInputsJson] = useState("");

  useEffect(() => {
    if (resultId) {
      loadResult();
    }
  }, [resultId]);

  // Poll for progress if workflow is still running
  useEffect(() => {
    if (
      !savedResult ||
      savedResult.workflowStatus === "completed" ||
      savedResult.workflowStatus === "failed"
    ) {
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        const res = await apiClient.post<{ result: SavedResult }>(
          `/admin/email-automation/results/get`,
          { id: resultId }
        );
        setSavedResult(res.result);

        if (
          res.result.workflowStatus === "completed" ||
          res.result.workflowStatus === "failed"
        ) {
          clearInterval(pollInterval);
          if (res.result.workflowStatus === "completed") {
            showSnackbar("Pipeline completed!", 3000, "success");
          } else {
            showSnackbar(
              `Pipeline failed: ${res.result.errorMessage || "Unknown error"}`,
              4000,
              "error"
            );
          }
        }
      } catch (error) {
        console.error("Error polling result:", error);
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [savedResult, resultId, showSnackbar]);

  // Poll for progress when running a new pipeline
  useEffect(() => {
    if (!runProgress || runProgress.status === "completed" || runProgress.status === "failed") {
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        const res = await apiClient.post<{ result: SavedResult }>(
          `/admin/email-automation/results/get`,
          { id: runProgress.resultId }
        );
        const result = res.result;

        setRunProgress((prev) =>
          prev
            ? {
                ...prev,
                processedCount: result.processedCount,
                totalCount: result.totalCount,
                status: result.workflowStatus,
              }
            : null
        );

        if (result.workflowStatus === "completed" || result.workflowStatus === "failed") {
          clearInterval(pollInterval);
          if (result.workflowStatus === "completed") {
            showSnackbar(
              `Pipeline completed: ${result.processedCount} email(s) processed`,
              4000,
              "success"
            );
            // If overwriting, refresh current page; otherwise navigate to new result
            if (runProgress.isOverwrite) {
              setSavedResult(result);
            } else {
              navigate(`/admin/email-automation/result/${runProgress.resultId}`);
            }
          } else {
            showSnackbar(
              `Pipeline failed: ${result.errorMessage || "Unknown error"}`,
              4000,
              "error"
            );
          }
          setRunning(false);
          setRunDialogOpen(false);
          setRunProgress(null);
        }
      } catch (error) {
        console.error("Error polling progress:", error);
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [runProgress, navigate, showSnackbar]);

  const loadResult = async () => {
    try {
      setLoading(true);
      const res = await apiClient.post<{ result: SavedResult }>(
        `/admin/email-automation/results/get`,
        { id: resultId }
      );
      setSavedResult(res.result);
    } catch (error) {
      console.error("Error loading result:", error);
      showSnackbar("Failed to load result", 4000, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = async (result: PipelineResultLight, index: number) => {
    setSelectedResult(result);
    setDetailedResult(null);
    setDrawerOpen(true);

    if (resultId) {
      setLoadingDetails(true);
      try {
        const res = await apiClient.post<{ emailResult: PipelineResultFull }>(
          `/admin/email-automation/results/get-automation-result`,
          { resultId, input: result.input || { emailId: result.emailId }, resultIndex: index }
        );
        setDetailedResult(res.emailResult);
      } catch (error) {
        console.error("Error loading email result details:", error);
      } finally {
        setLoadingDetails(false);
      }
    }
  };

  const handleOpenEmail = (emailId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    dispatch(openEmailModal(emailId));
  };

  const handleOpenShipment = (shipmentId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    dispatch(openShipmentModal(shipmentId));
  };

  const handleOpenReminder = (reminderId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    dispatch(openDatabaseObjectModal({ table: "shipmentReminder", id: reminderId }));
  };

  const handleOpenEditName = () => {
    setEditName(savedResult?.name || "");
    setEditNameDialogOpen(true);
  };

  const handleSaveName = async () => {
    if (!resultId || !editName.trim()) return;

    setSaving(true);
    try {
      await apiClient.put(`/admin/email-automation/results/update`, {
        id: resultId,
        name: editName.trim(),
      });
      showSnackbar("Name updated successfully", 3000, "success");
      setEditNameDialogOpen(false);
      loadResult();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || "Failed to update name";
      showSnackbar(errorMessage, 4000, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotes = async (emailId: string, notes: string) => {
    if (!resultId) return;

    try {
      await apiClient.post(`/admin/email-automation/results/update-email-note`, {
        resultId,
        emailId,
        notes,
      });
      showSnackbar("Notes saved successfully", 2000, "success");

      // Update local state to reflect the change
      if (savedResult) {
        const updatedResults = savedResult.results.map((r) =>
          r.emailId === emailId ? { ...r, notes } : r
        );
        setSavedResult({ ...savedResult, results: updatedResults });
      }

      // Update selected result if it's the one being edited
      if (selectedResult && selectedResult.emailId === emailId) {
        setSelectedResult({ ...selectedResult, notes });
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || "Failed to save notes";
      showSnackbar(errorMessage, 4000, "error");
      throw error;
    }
  };

  const loadTestSets = async () => {
    try {
      const res = await apiClient.post<{ testSets: TestSet[] }>(
        `/admin/email-test-sets/list`,
        {}
      );
      setTestSets(res.testSets);
    } catch (error) {
      console.error("Error loading test sets:", error);
    }
  };

  const handleOpenRunDialog = () => {
    setRunDialogOpen(true);
    loadTestSets();

    const hasStructuredInputs = savedResult?.inputs?.some(
      (i: any) => i.shipmentId || i.reminderId
    );

    if (hasStructuredInputs) {
      setInputMode("jsonInputs");
      setInputsJson(JSON.stringify(savedResult!.inputs, null, 2));
      setEmailIds("");
      setSelectedTestSetId("");
    } else if (savedResult?.testSetId) {
      setInputMode("emailIds");
      setInputsJson("");
      setSelectedTestSetId(savedResult.testSetId);
      setEmailIds("");
    } else if (savedResult?.inputs?.length) {
      setInputMode("emailIds");
      setInputsJson("");
      setSelectedTestSetId("");
      const emailIdsFromInputs = (savedResult.inputs || [])
        .filter((i: any) => i.emailId)
        .map((i: any) => i.emailId);
      setEmailIds(emailIdsFromInputs.join("\n"));
    } else {
      setInputMode("emailIds");
      setInputsJson("");
    }

    // Pre-select the steps that were run previously
    const previousSteps = (savedResult?.stepsRun || []).filter(
      (s): s is PipelineStep => ALL_STEPS.includes(s as PipelineStep)
    );
    setStepsToRun(previousSteps.length > 0 ? previousSteps : ALL_STEPS);
    setRunProgress(null);
    setOverwriteResults(true); // Default to overwrite
  };

  const handleRunPipeline = async () => {
    if (!savedResult?.configId) return;

    if (inputMode === "jsonInputs") {
      let parsedInputs: any[];
      try {
        parsedInputs = JSON.parse(inputsJson);
        if (!Array.isArray(parsedInputs) || parsedInputs.length === 0) {
          showSnackbar("JSON must be a non-empty array of inputs", 3000, "error");
          return;
        }
      } catch {
        showSnackbar("Invalid JSON", 3000, "error");
        return;
      }

      setRunning(true);
      try {
        const res = await apiClient.post<{ resultId: string; workflowId: string }>(
          `/admin/email-automation/run`,
          {
            configId: savedResult.configId,
            inputs: parsedInputs,
            stepsToRun: stepsToRun.length === ALL_STEPS.length ? undefined : stepsToRun,
            overwriteResultId: overwriteResults ? resultId : undefined,
          }
        );

        setRunProgress({
          resultId: res.resultId,
          workflowId: res.workflowId,
          processedCount: 0,
          totalCount: parsedInputs.length,
          status: "pending",
          isOverwrite: overwriteResults,
        });

        showSnackbar("Pipeline started! Processing inputs...", 3000, "info");
      } catch (error: any) {
        const errorMessage = error?.response?.data?.error || "Failed to start pipeline";
        showSnackbar(errorMessage, 4000, "error");
        setRunning(false);
      }
      return;
    }

    const emailIdList = emailIds
      .split(/[,\s]+/)
      .map((id) => id.trim())
      .filter((id) => id.length > 0);

    if (emailIdList.length === 0 && !selectedTestSetId) {
      showSnackbar("Please enter email IDs or select a test set", 3000, "info");
      return;
    }

    setRunning(true);
    try {
      const res = await apiClient.post<{ resultId: string; workflowId: string }>(
        `/admin/email-automation/run`,
        {
          configId: savedResult.configId,
          inputs: emailIdList.length > 0 ? emailIdList.map(id => ({ emailId: id })) : undefined,
          testSetId: selectedTestSetId || undefined,
          stepsToRun: stepsToRun.length === ALL_STEPS.length ? undefined : stepsToRun,
          overwriteResultId: overwriteResults ? resultId : undefined,
        }
      );

      setRunProgress({
        resultId: res.resultId,
        workflowId: res.workflowId,
        processedCount: 0,
        totalCount: emailIdList.length || 0,
        status: "pending",
        isOverwrite: overwriteResults,
      });

      showSnackbar("Pipeline started! Processing emails...", 3000, "info");
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || "Failed to start pipeline";
      showSnackbar(errorMessage, 4000, "error");
      setRunning(false);
    }
  };

  const handleStepToggle = (step: PipelineStep) => {
    setStepsToRun((prev) =>
      prev.includes(step) ? prev.filter((s) => s !== step) : [...prev, step]
    );
  };

  const escapeCsvValue = (value: string): string => {
    if (value.includes(",") || value.includes('"') || value.includes("\n")) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  };

  const handleExportCsv = () => {
    if (!savedResult || results.length === 0) return;

    const headers = [
      "Email ID",
      "Email Subject",
      "Categories",
      "Properties (preview)",
      "API Data Keys",
      "Presentation (preview)",
      "Eval",
      "Notes",
      "Errors",
      "Warnings",
      "GPT Request Count",
    ];

    const rows = results.map((result) => {
      const categories = result.categorizationStep?.categories.join("; ") || "";
      const properties =
        result.propertyExtractionStep?.properties
          .map((p) => `${p.name}: ${p.value}`)
          .join("; ") || "";
      const propertyCount = result.propertyExtractionStep?.totalCount || 0;
      const propertiesWithCount =
        propertyCount > 3 ? `${properties} (+${propertyCount - 3} more)` : properties;
      const apiDataKeys = result.apiFetchStep?.dataKeys?.join("; ") || "";
      const presentation = result.presentationStep?.presentationPreview || "";
      const evalJson = result.evalStep?.output
        ? JSON.stringify(result.evalStep.output)
        : "";
      const counts = getPipelineDiagnosticCounts(result);

      return [
        result.emailId,
        result.emailSubject || "",
        categories,
        propertiesWithCount,
        apiDataKeys,
        presentation,
        evalJson,
        result.notes || "",
        counts.errors.toString(),
        counts.warns.toString(),
        result.gptRequestCount.toString(),
      ].map(escapeCsvValue);
    });

    const csvContent = [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    const filename = savedResult.name
      ? `${savedResult.name.replace(/[^a-z0-9]/gi, "_")}.csv`
      : `pipeline_results_${resultId}.csv`;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckIcon color="success" />;
      case "failed":
        return <ErrorIcon color="error" />;
      case "running":
        return <PlayIcon color="primary" />;
      default:
        return <PendingIcon color="disabled" />;
    }
  };

  if (loading) {
    return (
      <AdminPage title="Pipeline Results">
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      </AdminPage>
    );
  }

  if (!savedResult) {
    return (
      <AdminPage title="Pipeline Results">
        <Box sx={{ p: 3 }}>
          <Alert severity="error">Result not found</Alert>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate("/admin/email-automation")}
            sx={{ mt: 2 }}
          >
            Back to Pipeline Config
          </Button>
        </Box>
      </AdminPage>
    );
  }

  const results = savedResult.results || [];
  const isRunning =
    savedResult.workflowStatus === "running" || savedResult.workflowStatus === "pending";

  return (
    <AdminPage
      title={savedResult.name || `Results: ${savedResult.configSnapshot?.name || "Unknown"}`}
    >
      <Box sx={{ p: 3 }}>
        {/* Header Actions */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate(`/admin/email-automation/config/${savedResult.configId}`)}
          >
            Back to Pipeline Config
          </Button>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<PlayIcon />}
              onClick={handleOpenRunDialog}
            >
              Run Again
            </Button>
            {results.length > 0 && (
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportCsv}>
                Export CSV
              </Button>
            )}
            <Button variant="outlined" startIcon={<EditIcon />} onClick={handleOpenEditName}>
              Edit Name
            </Button>
          </Box>
        </Box>

        {/* Title and ID */}
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Typography variant="h5">Pipeline Results</Typography>
          <DbObjectIdComponent
            value={savedResult.id}
            table="emailAutomationPipelineResult"
            clipboard
            cropText
            prefix="ID: "
          />
        </Box>

        {/* Status Bar */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box display="flex" alignItems="center" gap={2}>
            {getStatusIcon(savedResult.workflowStatus)}
            <Box flex={1}>
              <Typography variant="body2">
                Status: <strong>{savedResult.workflowStatus}</strong>
              </Typography>
              {isRunning && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={
                        savedResult.totalCount > 0
                          ? (savedResult.processedCount / savedResult.totalCount) * 100
                          : 0
                      }
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {savedResult.processedCount} / {savedResult.totalCount}
                  </Typography>
                </Box>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary">
              {results.length} of {savedResult.totalCount} email(s)
            </Typography>
          </Box>
          {/* Steps Run */}
          {savedResult.stepsRun && savedResult.stepsRun.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Steps run:{" "}
                {savedResult.stepsRun
                  .map((s) => PIPELINE_STEPS[s as PipelineStep]?.label ?? s)
                  .join(", ")}
              </Typography>
            </Box>
          )}
          {savedResult.errorMessage && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {savedResult.errorMessage}
            </Alert>
          )}
          {/* Eval Summary */}
          {(() => {
            const evalSummary = computeEvalSummary(results);
            if (evalSummary.total === 0) return null;
            return (
              <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Eval Results</strong> (total: {evalSummary.total})
                </Typography>
                {evalSummary.keys.map(({ key, values }) => (
                  <Typography
                    key={key}
                    variant="body2"
                    color="text.secondary"
                    component="div"
                    sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}
                  >
                    • {key}:{" "}
                    {values.map((v, idx) => {
                      const color = getValueColor(v.value);
                      return (
                        <React.Fragment key={v.value}>
                          {idx > 0 && ", "}
                          <Box
                            component="span"
                            sx={color ? { color, fontWeight: 600 } : undefined}
                          >
                            {truncateValue(v.value)}
                          </Box>
                          {" "}({v.count})
                        </React.Fragment>
                      );
                    })}
                  </Typography>
                ))}
              </Box>
            );
          })()}
        </Paper>

        {/* Results Table or Empty State */}
        {results.length === 0 && !isRunning ? (
          <Alert severity="info">No results available yet.</Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Click a row to view details.
            </Typography>
            <ResultsTable
              results={results}
              selectedEmailId={selectedResult?.emailId}
              onRowClick={handleRowClick}
              onOpenEmail={handleOpenEmail}
              onOpenShipment={handleOpenShipment}
              onOpenReminder={handleOpenReminder}
              onSaveNotes={handleSaveNotes}
            />
          </>
        )}

        {/* Result Detail Drawer */}
        <ResultDetailDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          selectedResult={selectedResult}
          detailedResult={detailedResult}
          loadingDetails={loadingDetails}
          onOpenEmail={handleOpenEmail}
          onSaveNotes={handleSaveNotes}
          activeTab={drawerTab}
          onTabChange={setDrawerTab}
        />

        {/* Edit Name Dialog */}
        <EditNameDialog
          open={editNameDialogOpen}
          onClose={() => setEditNameDialogOpen(false)}
          name={editName}
          setName={setEditName}
          saving={saving}
          onSave={handleSaveName}
        />

        {/* Run Pipeline Dialog */}
        <RunPipelineDialog
          open={runDialogOpen}
          onClose={() => setRunDialogOpen(false)}
          title="Run Again"
          testSets={testSets}
          selectedTestSetId={selectedTestSetId}
          setSelectedTestSetId={setSelectedTestSetId}
          emailIds={emailIds}
          setEmailIds={setEmailIds}
          stepsToRun={stepsToRun}
          onStepToggle={handleStepToggle}
          running={running}
          runProgress={runProgress}
          onRunPipeline={handleRunPipeline}
          overwriteResults={overwriteResults}
          setOverwriteResults={setOverwriteResults}
          inputMode={inputMode}
          setInputMode={setInputMode}
          inputsJson={inputsJson}
          setInputsJson={setInputsJson}
        />
      </Box>
    </AdminPage>
  );
}
